import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAiUsage } from '../../api/services/aiService';

const TONE = { ok: 'bg-primary', warning: 'bg-warning', exceeded: 'bg-danger' };

/** Loads the workspace's monthly AI-reply usage and keeps it fresh while the page is open. */
export function useAiUsage({ enabled = true, pollMs = 15000 } = {}) {
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const reload = useCallback(() => getAiUsage().then((u) => { setUsage(u); setError(''); }).catch((e) => setError(e.message || 'Could not load AI usage.')), []);
  useEffect(() => {
    if (!enabled) return undefined;
    reload();
    const timer = window.setInterval(reload, pollMs);
    return () => window.clearInterval(timer);
  }, [enabled, pollMs, reload]);
  return { usage, error, reload };
}

export const fmt = (n) => Number(n || 0).toLocaleString('en-US');

export function UsageBar({ usage }) {
  return (
    <div className="progress" style={{ height: 12 }} role="progressbar" aria-label="AI replies used this month" aria-valuenow={usage.percent} aria-valuemin={0} aria-valuemax={100}>
      <div className={`progress-bar ${TONE[usage.status]}`} style={{ width: `${Math.max(usage.percent, usage.used ? 2 : 0)}%` }} />
    </div>
  );
}

/** Slim banner shown across the dashboard when the workspace is close to, or past, its monthly AI quota. */
export function AiQuotaBanner({ usage }) {
  const [hidden, setHidden] = useState('');
  if (!usage || usage.status === 'ok' || hidden === usage.status) return null;
  const exceeded = usage.status === 'exceeded';
  return (
    <div className={`alert ${exceeded ? 'alert-danger' : 'alert-warning'} d-flex align-items-center justify-content-between gap-3 mb-0 rounded-0 border-0 border-bottom`} role="alert">
      <div>
        <strong>{exceeded ? 'AI replies used up.' : `${usage.percent}% of AI replies used.`}</strong>{' '}
        {exceeded
          ? 'New visitor chats are being handed to your human agents until the quota resets or you upgrade.'
          : `${fmt(usage.remaining)} replies left this month on the ${usage.plan} plan.`}{' '}
        <Link to="/ai-usage" className="alert-link">View AI usage</Link>
      </div>
      <button type="button" className="btn-close" aria-label="Dismiss" onClick={() => setHidden(usage.status)} />
    </div>
  );
}

/** Compact meter used inside Bot Configuration. */
export function AiUsageMini({ usage }) {
  if (!usage) return null;
  return (
    <div className="ai-mini">
      <div className="d-flex justify-content-between mb-1"><span>AI replies this month</span><strong>{fmt(usage.used)} / {fmt(usage.quota)}</strong></div>
      <UsageBar usage={usage} />
      <Link to="/ai-usage" className="small">See full usage</Link>
    </div>
  );
}
